import PlatformSupportIcons from "../../../components/PlatformSupportIcons/PlatformSupportIcons";
import { useSiteData } from "../../../contexts/SiteDataContext";
import type {
  BooleanFieldItem,
  ComplexBooleanFieldItem,
  Vpn,
  VpnDetailItem,
} from "../../../types";
import DynamicField from "./DynamicField";

interface RatingPageVpnCardDetailsProps {
  vpn: Vpn;
  isOpen: boolean;
}

function getVpnFieldPositiveValue(
  item: ComplexBooleanFieldItem | BooleanFieldItem,
): boolean {
  if (item.type === "complex_boolean_field") {
    return item.isPositive;
  }
  return item.value;
}

function RatingPageVpnCardDetails({
  vpn,
  isOpen,
}: RatingPageVpnCardDetailsProps) {
  const spoilerClass = `overflow-hidden transition-[max-height] duration-200 ease-in-out ${isOpen ? "max-h-[96rem]" : "max-h-0"}`;

  const { siteData } = useSiteData();

  const {
    payment_with_crypto,
    is_free,
    zero_tracking,
    jurisdiction_type,
    jurisdiction_country,
    requires_personal_data,
    obsfucation,
    has_support,
    alternative_clients,
    split_tunneling,
    star_verified,
  } = vpn;

  const positiveZeroTracking = Boolean(zero_tracking);
  const positiveDemocratic = jurisdiction_type === "democratic";
  const positivePersonalData = !requires_personal_data;
  const postitiveObsfucation = Boolean(obsfucation);
  const positiveSupport = Boolean(has_support && has_support !== "none");
  const positiveAlternativeClients = Boolean(alternative_clients);
  const positiveSplitTunneling = Boolean(split_tunneling);
  const positiveStarVerified = Boolean(star_verified);
  const positivePayment = Boolean(is_free || payment_with_crypto);
  const jusrisdictionCountryName = jurisdiction_country?.name;

  const vpnItems: VpnDetailItem[] = [
    // First row on the card, and the only one that can be absent: the tick is
    // a claim we make, not a criterion we score, so an unverified service says
    // nothing rather than saying "Нет". Everything below shifts up a slot when
    // it is gone; the desktop slice below stays at 6 either way, so the
    // collapsed card keeps two full rows of three and just shows one more
    // field (Техподдержка) on unverified services.
    ...(positiveStarVerified
      ? [
          {
            label: "Верифицировано Звездой НА СВЯЗИ",
            value: true,
            yesText: "Да",
            type: "boolean_field",
          } as const,
        ]
      : []),
    {
      label: "Юрисдикция",
      isPositive: positiveDemocratic,
      hintText: siteData?.hint_jusrisdiction?.content,
      yesText: jusrisdictionCountryName,
      noText: jusrisdictionCountryName,
      type: "complex_boolean_field",
    },
    {
      label: "Политика No log/Zero tracking",
      isPositive: positiveZeroTracking,
      hintText: siteData?.hint_zero_tracking?.content,
      type: "complex_boolean_field",
    },
    {
      label: "Обфускация (маскировка)",
      isPositive: postitiveObsfucation,
      hintText: siteData?.hint_obsfucation?.content,
      type: "complex_boolean_field",
    },
    {
      label: "Возможность анонимной оплаты (криптой)",
      isPositive: positivePayment,
      hintText: siteData?.hint_has_crypto?.content,
      yesText: is_free ? "Бесплатный" : "Да",
      noText: "Нет",
      type: "complex_boolean_field",
    },
    {
      label: "Требует персональные данные при регистрации",
      isPositive: positivePersonalData,
      hintText: siteData?.hint_requires_personal_data?.content,
      type: "complex_boolean_field",
      yesText: "Нет",
      noText: "Да",
    },
    {
      label: "Техподдержка",
      value: positiveSupport,
      type: "boolean_field",
    },
    {
      label: "Альтернативные клиенты",
      value: positiveAlternativeClients,
      noText: "Отсутствуют",
      type: "boolean_field",
    },
    {
      label: "Раздельное туннелирование",
      value: positiveSplitTunneling,
      type: "boolean_field",
    },
    {
      label: "Поддерживаемые протоколы",
      value: vpn.protocols.map((protocol) => protocol.name).join(", "),
      type: "labeled_field",
    },
  ];

  const sortedVpnItemsExceptLabeled = vpnItems.filter(
    (item) => item.type !== "labeled_field",
  );
  const sortedVpnItemsForMobile = [
    ...sortedVpnItemsExceptLabeled.filter(
      (item) => !getVpnFieldPositiveValue(item),
    ),
    ...sortedVpnItemsExceptLabeled.filter((item) =>
      getVpnFieldPositiveValue(item),
    ),
    ...vpnItems.filter((item) => item.type === "labeled_field"),
  ];

  const gridSpoilerScheme = {
    mobileAboveSpolier: sortedVpnItemsForMobile.slice(0, 2),
    mobileBelowSpolier: sortedVpnItemsForMobile.slice(2),
    desktopAboveSpolier: vpnItems.slice(0, 6),
    desktopBelowSpolier: vpnItems.slice(6),
  };

  return (
    <div className="text-left text-sm font-normal">
      <div className="grid grid-cols-2 gap-3 md:hidden">
        {gridSpoilerScheme.mobileAboveSpolier.map((item) => {
          return <DynamicField item={item} key={item.label} />;
        })}
      </div>
      <div className="grid-cols-3 gap-3 hidden md:grid">
        {gridSpoilerScheme.desktopAboveSpolier.map((item) => {
          return <DynamicField item={item} key={item.label} />;
        })}
      </div>

      <div className={spoilerClass}>
        <div className="mt-3"></div>

        <div className="grid grid-cols-2 gap-3 md:hidden">
          {gridSpoilerScheme.mobileBelowSpolier.map((item) => {
            return <DynamicField item={item} key={item.label} />;
          })}
        </div>
        <div className="grid-cols-3 gap-3 hidden md:grid">
          {gridSpoilerScheme.desktopBelowSpolier.map((item) => {
            return <DynamicField item={item} key={item.label} />;
          })}
        </div>

        {vpn.comment && (
          <>
            <div className="mt-6 pt-3 md:border-t border-almost-white">
              <p className="font-semibold text-sm mb-2">
                {siteData?.vpn_card_comment?.title}
              </p>
              {/* Operators paste raw URLs in here; without break-words a long
                  one overflows the card instead of wrapping. */}
              <p className="text-sm font-normal whitespace-pre-line break-words">
                {vpn.comment}
              </p>
            </div>
          </>
        )}

        <PlatformSupportIcons
          vpn={vpn}
          className="mt-3 justify-center items-center flex md:hidden md:border-b-0 border-light-blue-3 pb-3"
        />
      </div>
    </div>
  );
}

export default RatingPageVpnCardDetails;
